import React, { useEffect, useState } from 'react';
import api from '../services/api';
import styles from '../styles/App.module.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(null);
  const [error, setError] = useState('');

  // no per-row editable TIN/Password in admin list — view only

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/users/list'); // returns all users for admin
      const all = res.data || [];
      setUsers(all);
      // consultants: include role Consultant and Admin (admins can act as consultants)
      setConsultants(all.filter(u => String(u.role).toLowerCase() === 'consultant' || String(u.role).toLowerCase() === 'admin'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const changeRole = async (id, role) => {
    setChanging(id);
    setError('');
    try {
      const res = await api.put(`/users/${id}/role`, { role });
      setUsers(prev => prev.map(x => x.id === id ? { ...x, role: res.data.role } : x));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to change role');
    } finally {
      setChanging(null);
    }
  };

  const assignConsultant = async (userId, consultantId) => {
    setChanging(userId);
    setError('');
    try {
      if (!consultantId) {
        // unassign
        await api.post('/consultants/unassign', { userId });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, consultant_id: null } : u));
      } else {
        await api.post('/consultants/assign', { userId, consultantId });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, consultant_id: Number(consultantId) } : u));
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Assign failed');
    } finally {
      setChanging(null);
    }
  };

  // safe date formatter for 'YYYY-MM-DD' and ISO strings
  const fmtDate = (v) => {
    if (!v) return '-';
    const s = String(v).split('T')[0];
    const parts = s.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const [y, m, d] = parts;
      return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
    }
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return String(v).slice(0, 10);
    return dt.toLocaleDateString();
  };

  const consultantName = (u) => {
    const id = (u.consultant_id ?? u.consultantId ?? u.consultant) || null;
    if (!id) return '-';
    const c = users.find(x => Number(x.id) === Number(id));
    if (c) return c.name || c.email || `#${c.id}`;
    return `#${id}`;
  };

  const paymentStatus = (u) => {
    if (u.payment_status) return u.payment_status;
    if (u.is_paid !== undefined) return u.is_paid ? 'Paid' : 'Unpaid';
    if (u.payment && typeof u.payment.status === 'string') return u.payment.status;
    return 'Unknown';
  };

  return (
    <div>
      <h3>Users</h3>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div style={{ marginTop: 8 }}>
        <div className={styles.tableWrapper} style={{ overflowX: 'auto' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>SL</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>TIN</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Mobile Number</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Date of Joining</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Consultant Name</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f6f6f6' }}>
                    <td style={{ padding: 8 }}>{idx + 1}</td>
                    <td style={{ padding: 8 }}>
                      <div style={{ fontWeight: 700 }}>{u.name || u.email}</div>
                    </td>
                    <td style={{ padding: 8 }}>{u.tin ?? '-'}</td>
                    <td style={{ padding: 8 }}>{u.mobile || u.registered_mobile || '-'}</td>
                    <td style={{ padding: 8 }}>
                      <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={changing === u.id}>
                        <option value="User">User</option>
                        <option value="Consultant">Consultant</option>
                        <option value="Admin">Admin</option>
                      </select>
                      {changing === u.id && <div style={{ fontSize: 12, color: '#666' }}>Updating...</div>}
                    </td>
                    <td style={{ padding: 8 }}>{fmtDate(u.created_at)}</td>
                    <td style={{ padding: 8 }}>
                      <select
                        value={u.consultant_id ?? ''}
                        onChange={(e) => assignConsultant(u.id, e.target.value || null)}
                        disabled={changing === u.id}
                      >
                        <option value=''>— None —</option>
                        {consultants.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.email} {c.role === 'Admin' ? '(Admin)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: 8 }}>{paymentStatus(u)}</td>
                  </tr>
                ))}
                {users.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} style={{ padding: 12, color: '#666' }}>No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;